import React, { useState, useRef, useEffect } from 'react';
import { LuListChecks, LuLoaderCircle, LuSendHorizontal } from 'react-icons/lu';
import CommandsContainer from '../utils/CommandsContainer.jsx';
import { ASKME_COMMANDS } from '../../constants/askme_commands.js';

const InputBox = ({
  value,
  onChange,
  onSendMessage,
  isLoading,
  placeholder = "Type your question…"
}) => {
  const [rows, setRows] = useState(1);
  const [showCommands, setShowCommands] = useState(false);
  const [commandSearchTerm, setCommandSearchTerm] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    const textarea = e.target;
    
    // Check for command trigger
    const trimmedValue = newValue.trim();
    if (trimmedValue.startsWith('/')) {
      setShowCommands(true);
      setCommandSearchTerm(trimmedValue.substring(1)); // Remove the '/' for filtering
    } else {
      setShowCommands(false);
      setCommandSearchTerm('');
    }
    
    computeInputHeight(textarea);
  };

  const computeInputHeight = (textarea) => {
    // Reset height to calculate scrollHeight properly
    textarea.style.height = 'auto'; 
    const scrollHeight = textarea.scrollHeight;
    
    // Get the actual line height from computed styles
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    
    // Calculate the content height (excluding padding)
    const contentHeight = scrollHeight - paddingTop - paddingBottom;
    
    // Calculate the number of lines based on actual content height
    const actualLines = contentHeight / lineHeight;
    
    // Only increase rows when content actually exceeds current height
    // Use a more conservative approach - only grow when text clearly needs more space
    let newRows;
    if (actualLines <= 1.2) {
      newRows = 1; // Stay at 1 row for single line content
    } else if (actualLines <= 2.2) {
      newRows = 2; // Move to 2 rows when content clearly needs it
    } else {
      newRows = 3; // Max out at 3 rows
    }
    
    setRows(newRows);
    
    // Reset the height back to auto after calculation
    textarea.style.height = '';
  }

  const handleKeyDown = (e) => {
    // Handle escape key to close commands
    if (e.key === 'Escape' && showCommands) {
      e.preventDefault();
      setShowCommands(false);
      setCommandSearchTerm('');
      return;
    }
    
    // Handle backspace on exact command match
    if (e.key === 'Backspace') {
      const trimmedValue = value.trim();
      const isExactCommand = ASKME_COMMANDS.some(command => {
        // For commands without parameters, check against generated command
        // For commands with parameters, this comparison won't match
        return !command.hasParameters() && command.generateCommand() === trimmedValue;
      });
      
      if (isExactCommand) {
        e.preventDefault();
        onChange('/');
        setShowCommands(true);
        setCommandSearchTerm('');
        return;
      }
    }
    
    // If commands are showing, let CommandsContainer handle navigation
    if (showCommands && ['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
      return; // Let CommandsContainer handle these
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        onSendMessage();
      }
    }
  };

  // Handle command selection
  const handleCommandSelect = (finalCommand) => {
    // finalCommand is now the processed command string, ready to be sent
    onChange(finalCommand);
    setShowCommands(false);
    setCommandSearchTerm('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Recalculate height when value changes (including after command selection)
  useEffect(() => {
    if (inputRef.current) {
      computeInputHeight(inputRef.current);
    }
  }, [value]);

  // Handle clicking outside to close commands
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowCommands(false);
        setCommandSearchTerm('');
      }
    };

    if (showCommands) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCommands]);

  // Opens the ready-made questions list, the same as typing "/"
  const handleShowCommands = () => {
    onChange('/');
    setShowCommands(true);
    setCommandSearchTerm('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-end gap-2 rounded-xl border border-ui-input bg-ui-input-bg p-2 shadow-sm transition-[border-color,box-shadow] focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/20">
        <button
          type="button"
          onClick={handleShowCommands}
          disabled={isLoading}
          title="Ready-made questions"
          aria-label="Show ready-made questions"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink disabled:opacity-50"
        >
          <LuListChecks aria-hidden className="size-[18px]" />
        </button>

        <textarea
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Your question"
          rows={rows}
          className="min-h-9 flex-1 resize-none bg-transparent px-1 py-2 text-sm text-ui-ink outline-none placeholder:text-ui-ink-3"
          style={{ lineHeight: '1.25rem', maxHeight: '6rem' }}
          disabled={isLoading}
        />

        <button
          type="button"
          onClick={onSendMessage}
          disabled={!value.trim() || isLoading}
          aria-label="Send question"
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <LuLoaderCircle aria-hidden className="size-4 animate-spin" />
          ) : (
            <LuSendHorizontal aria-hidden className="size-4" />
          )}
          Ask
        </button>
      </div>

      <CommandsContainer
        isVisible={showCommands}
        onCommandSelect={handleCommandSelect}
        searchTerm={commandSearchTerm}
        position="top"
      />
    </div>
  );
};

export default InputBox;
